import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { DISPLAY_DOMAIN } from "../lib/brand";
import api from "../lib/api";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      // Backend always returns 200 — only network errors land here
      toast.error("Could not reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-4 mb-8">
          <img src="/logo.png" alt="EchoSense Capital Club" className="w-16 h-16 object-contain" />
          <div className="text-center">
            <span className="text-3xl font-semibold text-platinum font-serif">EchoSense Capital Club</span>
            <span className="text-platinum/50 text-sm block">{DISPLAY_DOMAIN}</span>
          </div>
        </Link>

        <Card className="bg-white border-slate-200 shadow-xl animate-fade-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif text-slate-900">
              {submitted ? "Check Your Inbox" : "Forgot Password"}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {submitted
                ? "If an account exists for that email, we've sent you a reset link."
                : "Enter your email and we'll send you a secure reset link."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-5 text-center" data-testid="forgot-password-success">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  The reset link will expire in <strong>30 minutes</strong> and can only be used once.
                  If you don't see the email, check your spam folder.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full" data-testid="forgot-back-to-login">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/60" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-white border-slate-200 text-slate-900 pl-9 placeholder:text-platinum/60"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-medium"
                  data-testid="forgot-submit-button"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>

                <div className="text-center">
                  <Link to="/login" className="text-burgundy hover:text-burgundy-light text-sm font-medium">
                    <ArrowLeft className="inline w-3.5 h-3.5 mr-1" /> Back to Sign In
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
